import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';

import {
    UI_FUNCTIONAL_TESTING_PATH,
    UI_SCHEMES_PATH,
    UI_STANDS_PATH,
    UI_TEST_RUN_PATH,
} from '@shared/constants/pathsConstants';
import { dateFormat } from '@shared/constants/otherConstants';
import i18next from 'i18next';
import i18n from '@app/locale/i18n';
import { LS_SELECTED_LANGUAGE } from '@shared/constants/storagesKeysConstants';
import { AppLanguage } from '@shared/types';
import { deepGet } from '@shared/utils/utils';
import { INITIAL_TABLE_PAGINATION } from '@shared/constants/initialDataConstants';

export const generateUniqueStringKey = (pre) => {
    return `${pre || ''}_${new Date().getTime()}`;
};

export const arrayToFormData = (array, formData, arrayFieldName) => {
    Array.isArray(array) &&
        array.forEach((item) => {
            formData.append(`${arrayFieldName}`, item);
        });
};

export const getDeepDifferentFields = (initialObject, changedObject) => {
    const difference = Object.keys(initialObject).filter((k) => !isEqual(initialObject[k], changedObject[k]));

    let resultObj = {};
    for (const key in changedObject) {
        if (difference.includes(key)) {
            resultObj[key] = changedObject[key];
        }
    }

    return resultObj;
};

export function formatDuration(durationInSeconds) {
    const hours = Math.floor(durationInSeconds / 3600);
    const minutes = Math.floor((durationInSeconds % 3600) / 60);
    const seconds = (durationInSeconds % 60).toFixed();

    let formattedTime = '';

    if (hours > 0) {
        formattedTime += hours + 'h ';
    }

    if (minutes > 0 || hours === 0) {
        formattedTime += minutes + 'm ';
    }

    if (seconds > 0 || (hours === 0 && minutes === 0)) {
        formattedTime += seconds + 's';
    }

    return formattedTime.trim();
}

export function scrollToTopOfAppContainer() {
    const appContainerElement = getAppContentElement();
    appContainerElement.scrollTo({
        top: 0,
        behavior: 'smooth',
    });
}

export function getAppContentElement() {
    return document.querySelector('.AppContent');
}

export function downloadFile(file) {
    let fileUrl = '';

    if (typeof file == 'string') {
        fileUrl = file;
    } else {
        fileUrl = URL.createObjectURL(file);
    }
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = fileUrl;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(fileUrl);
}

export function convertToTranslationKey(key) {
    if (typeof key !== 'string') {
        console.error('Key must be string');
        return '';
    }
    return key.toLowerCase().split(' ').join('_');
}

export function getSchemeLinkById(id, addOrigin) {
    const filterOption = 'pk';
    return getObjectLinkById(filterOption, id, addOrigin, UI_SCHEMES_PATH);
}

export function getStandLinkBySchemeId(id, addOrigin, filterOption) {
    return getObjectLinkById(filterOption, id, addOrigin, UI_STANDS_PATH);
}

export function getTestRunLinkByStandId(standId, testrunId, addOrigin) {
    const filterOption = 'stand';
    const testrunPath = `${UI_FUNCTIONAL_TESTING_PATH}/${UI_TEST_RUN_PATH}/${testrunId}`;
    return getObjectLinkById(filterOption, standId, addOrigin, testrunPath);
}

export function getObjectLinkById(filterOption, id, addOrigin, objectUIPath) {
    const base = `${addOrigin ? window.location.origin : ''}/${objectUIPath}`;
    const columnFilters = encodeURIComponent(JSON.stringify([{ [filterOption]: String(id) }]));
    return `${base}?${!addOrigin ? 'pageIndex=0&pageSize=25&' : ''}columnFilters=${columnFilters}`;
}

export const IP_PATTERN = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;

// IP in format xxx.xxx.xxx.xxx OR xxx.xxx.xxx.xxx/xx, if format provided only one variant will be valid
export const matchesIpRegex = (str, format) => {
    const ipBody = IP_PATTERN.source.replace(/^\^/, '').replace(/\$$/, '');
    const subnetPart = String.raw`(?:\/(?:[1-2]?\d|3[0-2]))`;

    const ipOnly = new RegExp(`^(?:${ipBody})$`);
    const ipSubnet = new RegExp(`^(?:${ipBody})${subnetPart}$`);
    const both = new RegExp(`^(?:${ipBody})(?:${subnetPart})?$`);

    switch (format) {
        case 'ip':
            return ipOnly.test(str);
        case 'subnet':
            return ipSubnet.test(str);
        default:
            return both.test(str);
    }
};

export const isEmptyObject = (obj) => {
    return Object.keys(obj).length === 0 && obj.constructor === Object;
};
export const uuidv4 = () => {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
        (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
    );
};
export const convertDateToHumanReadableFormat = (
    dateConstructorParam,
    options = {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    },
) => {
    const dateObj = new Date(dateConstructorParam);
    const locale = localStorage.getItem(LS_SELECTED_LANGUAGE) === AppLanguage.RU ? 'ru-RU' : 'en-US';
    return dateObj.toLocaleString(locale, options);
};

export function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export const toTitleCase = (str) => {
    if (typeof str !== 'string') {
        return str;
    }

    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};
export const transformStringForKey = (str) => {
    if (typeof str !== 'string') {
        return str;
    }

    return str.toLowerCase().replace(/ /g, '_');
};

export function enforceMinMax(inputEl) {
    if (inputEl.value !== '') {
        if (parseInt(inputEl.value) < parseInt(inputEl.min)) {
            inputEl.value = inputEl.min;
        }
        if (parseInt(inputEl.value) > parseInt(inputEl.max)) {
            inputEl.value = inputEl.max;
        }
    } else {
        inputEl.value = inputEl.min;
    }
}

export function isCaseInsensitiveEqual(str1, str2) {
    if (typeof str1 !== 'string' || typeof str2 !== 'string') {
        return false;
    }

    return str1.toUpperCase() === str2.toUpperCase();
}

export function getArrayLookupObject(array, key = 'pk') {
    let lookup = {};

    for (let i = 0, len = array.length; i < len; i++) {
        const lookupKey = key.split('.').length > 1 ? deepGet(array[i], key) : array[i][key];

        if (key !== 'pk') {
            lookup[lookupKey] ? lookup[lookupKey].push(i) : (lookup[lookupKey] = [i]);
        } else {
            lookup[lookupKey] = array[i];
        }
    }

    return lookup;
}

export const getStateFromUrlParams = (key, urlParams, disableUrlParams) => {
    const DEFAULT_TABLE_STATE = {
        sorting: [],
        columnFilters: [],
        globalFilter: '',
    };

    if (key === 'pagination') {
        return !disableUrlParams && !isNaN(Number(urlParams?.pageIndex)) && !isNaN(Number(urlParams?.pageSize))
            ? {
                  pageIndex: Math.abs(Number(urlParams.pageIndex)),
                  pageSize: Math.abs(Number(urlParams.pageSize)),
              }
            : INITIAL_TABLE_PAGINATION;
    }

    if (disableUrlParams) {
        return DEFAULT_TABLE_STATE[key];
    }

    if (urlParams[key]) {
        try {
            const valueFromUrlParams =
                urlParams[key].includes('{') || urlParams[key].includes('[')
                    ? JSON.parse(urlParams[key])
                    : urlParams[key];
            switch (key) {
                case 'columnFilters': {
                    return valueFromUrlParams
                        .map((item) => {
                            const res = {};
                            for (let key in item) {
                                if (item[key]) {
                                    res.id = key;
                                    res.value =
                                        key === 'createdAt' || key === 'last_testrun'
                                            ? dayjs(item[key])
                                            : decodeURIComponent(item[key]);
                                }
                            }
                            return res;
                        })
                        .filter((item) => item.id && item.value);
                }
                case 'sorting': {
                    return [{ id: valueFromUrlParams.split('-').join(''), desc: valueFromUrlParams.includes('-') }];
                }
                case 'globalFilter': {
                    return valueFromUrlParams;
                }
                default:
            }
        } catch (error) {
            console.error(error);
        }
    }
    return DEFAULT_TABLE_STATE[key];
};
export const addUrlParamsToLink = (link, urlParams) => {
    let res = '';
    for (let key in urlParams) {
        res += `&${key}=${urlParams[key]}`;
    }
    return link + '?' + res.slice(1);
};

export function dateFormatting(date, format = dateFormat) {
    return dayjs(date).locale(i18next.language).format(format);
}

export const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
    } else {
        window.prompt(i18n.t('insecure_copy_helper_text'), text);
    }
};
export const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
        return parts.pop().split(';').shift();
    }
};

export function isSameDate(d1, d2) {
    return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
}

export const calculateOptimalTickStep = (maxNumber, arrayLength) => {
    const unroundedTickSize = maxNumber / (arrayLength - 1 || 1);
    const x = Math.ceil(Math.log10(unroundedTickSize) - 1);
    const pow10x = Math.pow(10, x);
    return Math.ceil(unroundedTickSize / pow10x) * pow10x;
};

export async function copyLink(id, getLink, onBeforeCopy) {
    if (!id) return;

    if (onBeforeCopy) {
        onBeforeCopy();
    }

    const link = getLink(id);
    await copyText(link);
}

export function getDayTime(dayOffset = 0, minutesOffset = 0) {
    const currentDate = new Date();

    if (dayOffset) {
        currentDate.setDate(currentDate.getDate() + dayOffset);
    }

    currentDate.setMinutes(minutesOffset);
    currentDate.setSeconds(0);
    currentDate.setMilliseconds(0);

    return currentDate;
}

export function getFilterByStringFunction(fieldsToSearch, searchString) {
    return (dataItem) => {
        if (searchString) {
            for (const field of fieldsToSearch) {
                if (String(dataItem[field]).toLowerCase().includes(searchString.toLowerCase())) {
                    return true;
                }
            }

            return false;
        }

        return true;
    };
}
